/**
 * Explicit public result-link publisher.
 *
 * This hook never creates a link while an analysis is rendered. A caller must
 * display the selected fields and call `publish` only after the owner confirms
 * that exact public projection.
 */
import { useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authenticatedFetch } from '../../../packages/api-client/src/utils/authenticated-fetch';
import { captureEvent } from '../lib/analytics';
import { getAppUrl } from '../lib/url';
import { getEdgeFunctionUrl } from '../lib/api-url';
import {
  CreateSharedLinkResultDto,
  RevokeSharedLinkResultDto,
} from '@dramadetector/shared/schemas';

export const PUBLIC_RESULT_FIELDS = ['score', 'summary'] as const;
export type PublicResultField = (typeof PUBLIC_RESULT_FIELDS)[number];

export interface SharedLinkResult {
  /** Absolute short URL, populated only after explicit publication succeeds. */
  shortUrl: string | null;
  /** Transient bearer token retained only while this share modal remains open. */
  linkToken: string | null;
  isLoading: boolean;
  error: string | null;
  /** Publish precisely the result fields the owner has previewed and confirmed. */
  publish: (selectedFields: readonly PublicResultField[]) => Promise<string | null>;
  /** Make the already-issued explicit public result link unavailable. */
  revoke: () => Promise<boolean>;
  reset: () => void;
}

function isSelectedFieldList(value: readonly PublicResultField[]): boolean {
  return value.length > 0 &&
    value.length <= PUBLIC_RESULT_FIELDS.length &&
    new Set(value).size === value.length &&
    value.every((field) => PUBLIC_RESULT_FIELDS.includes(field));
}

export function useCreateSharedLink(analysisId: string | undefined): SharedLinkResult {
  const { accessToken } = useAuth();
  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setShortUrl(null);
    setLinkToken(null);
    setError(null);
  }, []);

  const publish = useCallback(async (selectedFields: readonly PublicResultField[]) => {
    if (!analysisId) {
      setError('Analysis is unavailable for sharing');
      return null;
    }

    if (!accessToken) {
      setError('Sign in to publish a public result link');
      return null;
    }

    if (!isSelectedFieldList(selectedFields)) {
      setError('Select at least one supported result field');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(getEdgeFunctionUrl('create-shared-link'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          analysis_id: analysisId,
          selected_fields: selectedFields,
        }),
      });

      if (!response.ok) {
        setError('Unable to publish this result link');
        captureEvent('Share_LinkCreationFailed', {
          status: response.status,
          selected_field_count: selectedFields.length,
        });
        return null;
      }

      const body = (await response.json()) as { data?: unknown };
      const parsed = CreateSharedLinkResultDto.safeParse(body.data);
      if (!parsed.success) {
        setError('Invalid response from share link service');
        captureEvent('Share_LinkCreationFailed', {
          status: response.status,
          selected_field_count: selectedFields.length,
        });
        return null;
      }

      const fullShortUrl = `${getAppUrl()}${parsed.data.shortUrl}`;
      setLinkToken(parsed.data.link_token);
      setShortUrl(fullShortUrl);
      captureEvent('Share_LinkPublished', {
        selected_field_count: selectedFields.length,
      });
      return fullShortUrl;
    } catch {
      setError('Network error publishing result link');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, analysisId]);

  const revoke = useCallback(async () => {
    if (!linkToken) {
      setError('No published result link is available to revoke');
      return false;
    }

    if (!accessToken) {
      setError('Sign in to revoke this public result link');
      return false;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(getEdgeFunctionUrl('revoke-shared-link'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ link_token: linkToken }),
      });

      if (!response.ok) {
        setError('Unable to revoke this result link');
        captureEvent('Share_LinkRevokeFailed', { status: response.status });
        return false;
      }

      const body = (await response.json()) as { data?: unknown };
      if (!RevokeSharedLinkResultDto.safeParse(body.data).success) {
        setError('Invalid response from share link service');
        captureEvent('Share_LinkRevokeFailed', { status: response.status });
        return false;
      }

      reset();
      captureEvent('Share_LinkRevoked', {});
      return true;
    } catch {
      setError('Network error revoking result link');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, linkToken, reset]);

  return { shortUrl, linkToken, isLoading, error, publish, revoke, reset };
}
